import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useXP } from "@/hooks/useXP";
import { useChallenges, CHALLENGES } from "@/hooks/useChallenges";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { XPBadge } from "@/components/game/XPDisplay";
import { 
  ArrowLeft, 
  Trophy, 
  Star,
  Gift,
  Check,
  Lock,
  Crown,
  Sparkles,
  ChevronDown,
  ChevronUp
} from "lucide-react";
import { useHaptic } from "@/hooks/useHaptic";
import { cn } from "@/lib/utils";

function ChallengeCard({ challenge, onClaim, haptic }) {
  const { 
    title, 
    description, 
    icon, 
    reward, 
    current, 
    target, 
    progress, 
    isComplete, 
    isClaimed, 
    canClaim 
  } = challenge;

  const handleClaim = (e) => {
    e.stopPropagation();
    e.preventDefault();
    
    console.log('[CARD] Claim button clicked:', challenge.id, 'canClaim:', canClaim, 'isComplete:', isComplete, 'isClaimed:', isClaimed);
    if (canClaim) {
      haptic.win();
      onClaim(challenge.id);
    } else {
      console.log('[CARD] Cannot claim - canClaim is false');
    }
  };

  return (
    <Card 
      className={cn(
        "p-3 transition-all",
        isClaimed 
          ? 'bg-success/10 border-success/30' 
          : canClaim 
            ? 'bg-warning/10 border-warning/50 shadow-lg animate-pulse' 
            : 'bg-card border-border'
      )}
    >
      <div className="flex items-center gap-3">
        {/* Icon */}
        <div className={cn(
          "w-10 h-10 rounded-lg flex items-center justify-center text-xl flex-shrink-0",
          isClaimed ? 'bg-success/20' : canClaim ? 'bg-warning/20' : 'bg-muted'
        )}>
          {icon}
        </div>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-display font-semibold text-sm text-foreground truncate">
              {title}
            </h3>
            {isClaimed && <Check className="w-4 h-4 text-success flex-shrink-0" />}
          </div>
          <p className="font-body text-xs text-muted-foreground truncate">{description}</p>
          
          {/* Progress bar */}
          <div className="mt-1.5">
            <Progress value={progress} className="h-1.5" />
            <div className="flex justify-between items-center mt-0.5">
              <span className="font-body text-[10px] text-muted-foreground">
                {current.toLocaleString()}/{target.toLocaleString()}
              </span>
              <span className="font-display text-[10px] text-warning font-semibold">
                +{reward.toLocaleString()} XP
              </span>
            </div>
          </div>
        </div>
        
        {/* Claim button */}
        {canClaim && (
          <Button
            onClick={(e) => handleClaim(e)}
            size="sm"
            className="bg-warning hover:bg-warning/90 text-white font-display font-semibold shadow-md flex-shrink-0 h-8 px-2 z-10 relative"
          >
            <Gift className="w-3 h-3 mr-1" />
            Claim
          </Button>
        )}
        
        {isClaimed && (
          <span className="text-[10px] text-success font-body flex-shrink-0">✓</span>
        )}
      </div>
    </Card>
  );
}

// Collapsible challenge section
function ChallengeSection({ title, icon: Icon, iconColor, challenges, onClaim, haptic, defaultOpen = false }) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  const completedCount = challenges.filter(c => c.isClaimed).length;
  const claimableCount = challenges.filter(c => c.canClaim).length;
  const totalCount = challenges.length;
  
  return (
    <div className="mb-4">
      {/* Section Header - Clickable */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "w-full flex items-center justify-between p-3 rounded-xl transition-all",
          "bg-gradient-to-r from-muted/50 to-muted/30 hover:from-muted/70 hover:to-muted/50",
          "border border-border/50",
          claimableCount > 0 && "border-warning/50 bg-warning/5"
        )}
      >
        <div className="flex items-center gap-2">
          <Icon className={cn("w-5 h-5", iconColor)} />
          <span className="font-display font-bold text-foreground">{title}</span>
          <span className="text-xs text-muted-foreground">
            ({completedCount}/{totalCount})
          </span>
          {claimableCount > 0 && (
            <span className="bg-warning text-white text-[10px] px-1.5 py-0.5 rounded-full font-bold animate-pulse">
              {claimableCount} to claim!
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {isOpen ? (
            <ChevronUp className="w-5 h-5 text-muted-foreground" />
          ) : (
            <ChevronDown className="w-5 h-5 text-muted-foreground" />
          )}
        </div>
      </button>
      
      {/* Collapsible Content */}
      <div className={cn(
        "overflow-hidden transition-all duration-300 ease-in-out",
        isOpen ? "max-h-[5000px] opacity-100 mt-2" : "max-h-0 opacity-0"
      )}>
        <div className="space-y-2">
          {challenges.map(challenge => (
            <ChallengeCard 
              key={challenge.id} 
              challenge={challenge} 
              onClaim={onClaim}
              haptic={haptic}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export default function ChallengesPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { totalXP, level, currentXP, neededXP, prestige, addXP } = useXP();
  const { getAllChallengesWithStatus, claimChallenge, getChallengesCounts, stats } = useChallenges();
  const haptic = useHaptic();
  
  const { regular, master } = getAllChallengesWithStatus(prestige);
  const counts = getChallengesCounts(prestige);
  
  // Group challenges by category for better organization
  const groupChallenges = (challenges) => {
    const fruitTypes = ['banana', 'apple', 'pear', 'pineapple', 'orange', 'peach', 'strawberry', 'lemon', 'cherry', 'watermelon', 'mango', 'kiwi', 'blueberry', 'total_fruits'];
    const specialTypes = ['disco_combos', 'striped_fruits', 'wrapped_fruits', 'special_combos'];
    const progressTypes = ['levels_completed', 'perfect_wins', 'total_xp'];
    const prestigeTypes = ['prestige'];
    
    const groups = {
      fruits: [],
      special: [],
      progress: [],
      prestige: []
    };
    
    challenges.forEach(c => {
      if (fruitTypes.includes(c.type)) {
        groups.fruits.push(c);
      } else if (specialTypes.includes(c.type)) {
        groups.special.push(c);
      } else if (progressTypes.includes(c.type)) {
        groups.progress.push(c);
      } else if (prestigeTypes.includes(c.type)) {
        groups.prestige.push(c);
      } else {
        // Default to progress for unknown types
        groups.progress.push(c);
      }
    });
    
    return groups;
  };
  
  const regularGroups = groupChallenges(regular);
  const masterGroups = groupChallenges(master);

  const handleClaim = (challengeId) => {
    claimChallenge(challengeId, addXP);
  };

  // Check if any section has claimable rewards
  const hasClaimable = (challenges) => challenges.some(c => c.canClaim);

  return (
    <div className="min-h-screen game-gradient-bg pattern-dots">
      <div className="container mx-auto px-4 py-4 max-w-2xl">
        {/* Header */}
        <header className="flex items-center justify-between mb-4">
          <Button
            onClick={() => navigate('/')}
            variant="ghost"
            size="sm"
            className="font-body"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          <h1 className="font-display font-bold text-xl text-foreground flex items-center gap-2">
            <Trophy className="w-5 h-5 text-warning" />
            Challenges
          </h1>
          <div className="w-16" />
        </header>

        {/* Compact Stats Card */}
        <Card className="p-3 mb-4 bg-card border border-primary/20">
          <div className="flex items-center justify-between">
            <XPBadge 
              level={level} 
              currentXP={currentXP} 
              neededXP={neededXP} 
              totalXP={totalXP}
              prestige={prestige}
              showDetails={false}
            />
            <div className="flex items-center gap-4 text-center">
              <div>
                <p className="font-display font-bold text-lg text-foreground">
                  {counts.completed}/{counts.total}
                </p>
                <p className="font-body text-[10px] text-muted-foreground">Completed</p>
              </div>
              <div>
                <p className="font-display font-bold text-lg text-warning">
                  {counts.canClaim}
                </p>
                <p className="font-body text-[10px] text-muted-foreground">To Claim</p>
              </div>
            </div>
          </div>
        </Card>

        {/* Claimable Alert */}
        {counts.canClaim > 0 && (
          <Card className="p-3 mb-4 bg-warning/20 border-2 border-warning">
            <div className="flex items-center gap-3">
              <Gift className="w-6 h-6 text-warning animate-bounce" />
              <div>
                <p className="font-display font-bold text-sm text-foreground">
                  {counts.canClaim} Reward{counts.canClaim > 1 ? 's' : ''} Available!
                </p>
                <p className="font-body text-xs text-muted-foreground">
                  Expand sections below to claim
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Regular Challenges - Grouped */}
        <div className="mb-6">
          <h2 className="font-display font-bold text-base text-foreground mb-3 flex items-center gap-2">
            <Trophy className="w-4 h-4 text-primary" />
            Regular Challenges
          </h2>
          
          {regularGroups.fruits.length > 0 && (
            <ChallengeSection
              title="Fruit Collection"
              icon={Sparkles}
              iconColor="text-green-500"
              challenges={regularGroups.fruits}
              onClaim={handleClaim}
              haptic={haptic}
              defaultOpen={hasClaimable(regularGroups.fruits)}
            />
          )}
          
          {regularGroups.special.length > 0 && (
            <ChallengeSection
              title="Special Fruits"
              icon={Star}
              iconColor="text-yellow-500"
              challenges={regularGroups.special}
              onClaim={handleClaim}
              haptic={haptic}
              defaultOpen={hasClaimable(regularGroups.special)}
            />
          )}
          
          {regularGroups.progress.length > 0 && (
            <ChallengeSection
              title="Progress"
              icon={Trophy}
              iconColor="text-blue-500"
              challenges={regularGroups.progress}
              onClaim={handleClaim}
              haptic={haptic}
              defaultOpen={hasClaimable(regularGroups.progress)}
            />
          )}
        </div>

        {/* Master Challenges - Grouped */}
        <div className="mb-6">
          <h2 className="font-display font-bold text-base text-foreground mb-3 flex items-center gap-2">
            <Crown className="w-4 h-4 text-warning" />
            Master Challenges
          </h2>
          
          {masterGroups.fruits.length > 0 && (
            <ChallengeSection
              title="Fruit Mastery"
              icon={Sparkles}
              iconColor="text-emerald-500"
              challenges={masterGroups.fruits}
              onClaim={handleClaim}
              haptic={haptic}
              defaultOpen={hasClaimable(masterGroups.fruits)}
            />
          )}
          
          {masterGroups.special.length > 0 && (
            <ChallengeSection
              title="Special Mastery"
              icon={Star}
              iconColor="text-amber-500"
              challenges={masterGroups.special}
              onClaim={handleClaim}
              haptic={haptic}
              defaultOpen={hasClaimable(masterGroups.special)}
            />
          )}
          
          {masterGroups.progress.length > 0 && (
            <ChallengeSection
              title="Progress Mastery"
              icon={Trophy}
              iconColor="text-purple-500"
              challenges={masterGroups.progress}
              onClaim={handleClaim}
              haptic={haptic}
              defaultOpen={hasClaimable(masterGroups.progress)}
            />
          )}
          
          {masterGroups.prestige.length > 0 && (
            <ChallengeSection
              title="Prestige Goals"
              icon={Crown}
              iconColor="text-pink-500"
              challenges={masterGroups.prestige}
              onClaim={handleClaim}
              haptic={haptic}
              defaultOpen={hasClaimable(masterGroups.prestige)}
            />
          )}
        </div>

        {/* Total Rewards Info */}
        <Card className="p-3 bg-muted/50 border border-border">
          <div className="text-center">
            <p className="font-body text-xs text-muted-foreground mb-1">
              Total Potential XP
            </p>
            <p className="font-display font-bold text-xl text-warning">
              {[...CHALLENGES.regular, ...CHALLENGES.master]
                .reduce((sum, c) => sum + c.reward, 0)
                .toLocaleString()} XP
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
}
